import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Stack, Typography } from "@material-ui/core";
import { TravelExplore } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import useWebotServer from "hooks/useWebotServer";
import LinkedSourceCards from "./LinkedSourceCards";
import type { SourceLinkHint } from "./LinkedSourceCards";
import ListingSourceSearchDialog from "./ListingSourceSearchDialog";
import type { ListingSourceRef, REST_schema } from "../../../../@types/sharedSchema";

/**
 * "Which published marketplace ads are this same property?" — the listing form's section
 * for relating one listing to the marketplaces it is advertised on.
 *
 * One property is routinely advertised on several marketplaces at once, and a realtor
 * working it wants her ONE listing to stand for all of them: her board then shows her
 * corrected copy instead of each market ad.
 *
 * ## Two halves, and each was its own complaint
 *
 * - **Finding an ad** was a `Find` button that expanded eight un-scrollable rows into the
 *   middle of the form, with no filters, no paging and no way to collapse it again. It is
 *   now `ListingSourceSearchDialog`: a real search surface with the board's own filters.
 * - **Seeing what is linked** was one Chip per link, labelled with its ORIGIN ALONE — so
 *   three linked yad2 ads were three identical "יד2" chips. It is now
 *   `LinkedSourceCards`, which hydrates each link into a card.
 *
 * ## It suggests; she decides
 *
 * Nothing is ever linked automatically. An address key cannot answer "same property"
 * reliably — it was tried in production and split groups on missing fields — so the last
 * step is always a human who can see both ads. That is also why the search is allowed to
 * be generous: it ranks candidates that merely fail to contradict, and shows the ones
 * that DO contradict behind a toggle with the reason spelled out.
 *
 * ## Edit-mode only, deliberately
 *
 * Linking needs a listing id. On CREATE there is nothing to link to yet — and the
 * create-from-a-market-ad path already records its own link (see
 * `useLinkListingToMarketAd`). Rendering a dead picker on create would suggest an action
 * that cannot work. (The endpoint itself accepts an unsaved `draft`, so a create-mode
 * variant is possible later without a server change.)
 */

type ListingSourcePickerProps = {
  listingId: string;
  /** Current links, as stored on the listing. */
  value: ListingSourceRef<unknown>[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
};

export function ListingSourcePicker({
  listingId,
  value,
  onChange,
  disabled
}: ListingSourcePickerProps): JSX.Element {
  const { t: translate } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { linkListingSource, unlinkListingSource } = useWebotServer();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * Origin + url for links made in THIS session.
   *
   * `onChange` can only hand the form a list of ids, and the form rebuilds its refs from
   * that list by fabricating `{ origin: 'yad2' }` with no url for anything it has not seen
   * — so a freshly linked madlan ad rendered as "יד2", unopenable, until a reload. The
   * candidate we just linked carries both values; keeping them here is how the card gets
   * them without changing the form's contract.
   */
  const [hints, setHints] = useState<Record<string, SourceLinkHint>>({});

  const linkedIds = value.map((ref) => ref.listingId);

  const handleLink = useCallback(
    async (candidate: REST_schema.ListingSourceCandidate) => {
      setBusyId(candidate.id);
      try {
        const result = await linkListingSource(listingId, candidate.id);
        setHints((prev) => ({
          ...prev,
          [candidate.id]: {
            origin: candidate.origin,
            url: candidate.originalListingUrl ?? undefined
          }
        }));
        // The server returns the full set after the write, so the form never has to guess
        // how its optimistic merge turned out.
        onChange(result.sourceListingIds);
        enqueueSnackbar(translate("listingsPage.sourcePicker.linkedSuccess"), { variant: "success" });
      } catch (error) {
        console.error("Failed to link the marketplace ad:", error);
        enqueueSnackbar(translate("listingsPage.sourcePicker.linkFailed"), { variant: "error" });
      } finally {
        setBusyId(null);
      }
    },
    [listingId, linkListingSource, onChange, enqueueSnackbar, translate]
  );

  const handleUnlink = useCallback(
    async (sourceListingId: string) => {
      setBusyId(sourceListingId);
      try {
        const result = await unlinkListingSource(listingId, sourceListingId);
        onChange(result.sourceListingIds);
        // Detaching is not cosmetic — it puts the market original back on her board — and
        // it used to report nothing at all on success. The message says what changed.
        enqueueSnackbar(translate("listingsPage.sourcePicker.unlinkedSuccess"), { variant: "success" });
      } catch (error) {
        console.error("Failed to unlink the marketplace ad:", error);
        enqueueSnackbar(translate("listingsPage.sourcePicker.linkFailed"), { variant: "error" });
      } finally {
        setBusyId(null);
      }
    },
    [listingId, unlinkListingSource, onChange, enqueueSnackbar, translate]
  );

  /**
   * Move an ad from another of her listings onto this one.
   *
   * The state it replaces was a dead end: the row said "already linked to another of
   * your listings" over a disabled button, so freeing the ad meant opening her listings
   * one at a time until she found the holder. Both halves are ordinary owned-listing
   * writes she is already entitled to make; doing them from here is the difference
   * between a warning and a fix.
   *
   * ## Detach first, and it is deliberately NOT atomic
   *
   * There is no server operation that moves a link, and the two writes touch two
   * different documents. Detach-then-attach is the order whose interrupted state is
   * harmless AND visible: the ad ends up attached to nothing, which is a state the
   * picker already renders correctly and which one tap repairs. The opposite order can
   * transiently leave two of her listings claiming one ad — exactly the condition this
   * whole affordance exists to resolve — and the board would then hide the market
   * original on behalf of a listing she thought she had freed.
   */
  const handleMoveLink = useCallback(
    async (candidate: REST_schema.ListingSourceCandidate, fromListingId: string) => {
      setBusyId(candidate.id);
      let detached = false;
      try {
        await unlinkListingSource(fromListingId, candidate.id);
        detached = true;
        const result = await linkListingSource(listingId, candidate.id);
        setHints((prev) => ({
          ...prev,
          [candidate.id]: {
            origin: candidate.origin,
            url: candidate.originalListingUrl ?? undefined
          }
        }));
        onChange(result.sourceListingIds);
        enqueueSnackbar(translate("listingsPage.sourcePicker.movedSuccess"), { variant: "success" });
      } catch (error) {
        console.error("Failed to move the marketplace ad to this listing:", error);
        // Two genuinely different outcomes, and telling her which one happened is the
        // whole point: after a half-completed move the ad belongs to nobody, and she
        // needs to know that before she closes the modal.
        enqueueSnackbar(
          translate(
            detached
              ? "listingsPage.sourcePicker.moveHalfFailed"
              : "listingsPage.sourcePicker.moveFailed"
          ),
          { variant: "error" }
        );
      } finally {
        setBusyId(null);
      }
    },
    [listingId, linkListingSource, unlinkListingSource, onChange, enqueueSnackbar, translate]
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          {translate("listingsPage.sourcePicker.title")}
        </Typography>
        {value.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {translate("listingsPage.sourcePicker.linkedCount", { n: value.length })}
          </Typography>
        )}
      </Stack>

      {value.length === 0 && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {translate("listingsPage.sourcePicker.noLinks")}
        </Typography>
      )}

      <LinkedSourceCards
        value={value}
        hints={hints}
        disabled={disabled}
        busyId={busyId}
        onUnlink={handleUnlink}
      />

      <Button
        size="small"
        startIcon={<TravelExplore />}
        disabled={disabled}
        onClick={() => setDialogOpen(true)}
        sx={{ alignSelf: "flex-start" }}
      >
        {translate("listingsPage.sourcePicker.findVersions")}
      </Button>

      <ListingSourceSearchDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        target={{ listingId }}
        linkedIds={linkedIds}
        busyId={busyId}
        disabled={disabled}
        onLink={(candidate) => void handleLink(candidate)}
        onUnlink={(sourceListingId) => void handleUnlink(sourceListingId)}
        onMoveLink={(candidate, fromListingId) => void handleMoveLink(candidate, fromListingId)}
      />
    </Stack>
  );
}
